import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  Box,
  Card,
  CardProps,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import * as R from "ramda";
import React, { forwardRef, useEffect } from "react";
import { useAppId } from "../../../hook";
import {
  useCoordToId,
  useEventHandlers,
  useIdToCoord,
  useNavigateCoords,
} from "../../../hook/keyboardNavigation";
import { useMenu } from "../../../hook/menu";
import {
  newNotebookPage,
  useAddNotebookPageAsync,
  useDatabasePages,
  useSelectedNotebookPage,
} from "../../../hook/page";
import {
  useFetchDatabasePropertyValues,
  useFetchDatabasePropertyValuesListener,
  useFolderPropertyTypes,
  useGetProperty,
  useGetPropertyValue,
  useSetPropertyValue,
} from "../../../hook/page/property";
import { useSelectedDatabaseView } from "../../../hook/page/view";
import { useIsDarkMode } from "../../../hook/setting";
import { useFetchDatabasePagesListener } from "../../../hook/table/database";
import { Coord } from "../../../types/keyboardNavigation";
import { NotebookPage } from "../../../types/note";
import { BoardPageView, PageView } from "../../../types/notebook/view";
import {
  PropertyTypeKey,
  PropertyValue,
  PropertyValueKey,
} from "../../../types/page/property";
import { PropertyTypeSelect } from "../../../types/page/propertyType/select";
import { DynamicTextField } from "../../dynamicTextField";
import { KeyboardNavigationProvider } from "../../keyboardNavigation";
import { EditPageMenu } from "../../menu/page";
import { NotebookDatabaseToolBar, NotebookPageNoteElement } from "../view";
import { CardPropertyPills } from "../property/PropertyPill";

export const BoardCard = forwardRef<HTMLDivElement, CardProps>(
  ({ ...cardProps }, ref) => {
    const {
      sx: sxProp,
      style: styleProp,
      children,
      ...cardPropsRest
    } = cardProps;

    const style = {
      marginBottom: "0.5rem",
      padding: "1rem",
      width: "10rem",
      cursor: "grab",
      ...styleProp,
    };

    const sx = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      ...sxProp,
    };

    return (
      <Card ref={ref} sx={sx} elevation={5} style={style} {...cardPropsRest}>
        {children}
      </Card>
    );
  },
);

export const BoardCardPage: React.FC<{ pageId: string; coord: Coord; folderId: string }> = ({
  pageId,
  coord,
  folderId,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: pageId,
  });
  const coordToId = useCoordToId();
  const id = coordToId(coord);
  const handlers = useEventHandlers(id);
  const {
    handleOpen: editPageMenuHandleOpen,
    onClose: editPageMenuOnClose,
    open: editPageMenuOpen,
    anchorEl: editPageMenuAnchorEl,
    setAnchorEl: editPageMenuSetAnchorEl,
  } = useMenu();
  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };

  return (
    <>
      <EditPageMenu
        anchorEl={editPageMenuAnchorEl}
        onClose={editPageMenuOnClose}
        open={editPageMenuOpen}
        pageId={pageId}
      />
      <BoardCard
        data-testid={`board-card-${pageId}`}
        {...handlers}
        ref={setNodeRef}
        style={style}
        sx={{ flexDirection: "column", alignItems: "stretch" }}
        {...attributes}
        {...listeners}
        onContextMenu={(e) => {
          editPageMenuHandleOpen(e);
        }}
      >
        <NotebookPageNoteElement id={id} pageId={pageId} />
        <CardPropertyPills pageId={pageId} folderId={folderId} />
      </BoardCard>
    </>
  );
};

export const BoardCardAddPage: React.FC<{
  folderId: string;
  groupVal: string;
}> = ({ groupVal, folderId }) => {
  const [appId] = useAppId();
  const addPageAsync = useAddNotebookPageAsync();
  const id = `add-page-${groupVal}`;
  const eventHandlers = useEventHandlers(id);
  const idToCoord = useIdToCoord();
  const coord = idToCoord(id);
  const navigateCoords = useNavigateCoords();
  const coordToId = useCoordToId();
  const boardView = useSelectedDatabaseView() as BoardPageView;
  const groupBy = boardView["group-by"]!;
  const { folderProps } = useFolderPropertyTypes(folderId);
  const folderPropsDefined = folderProps ?? {};
  const prop = folderPropsDefined[groupBy] as PropertyTypeSelect;
  const setPropVal = useSetPropertyValue();

  return (
    <>
      <BoardCard data-testid={`board-add-card-${groupVal}`} {...eventHandlers}>
        <DynamicTextField
          id={id}
          placeholder="Page Name"
          text=""
          setText={(v) => {
            const page = newNotebookPage({ folderId, appId: appId!, name: v });
            addPageAsync(page);

            setPropVal(page["page-id"], prop.name, groupVal);
            setTimeout(() => {
              const targetId = page["page-id"];
              const targetElem = document.getElementById(targetId);
              targetElem?.dispatchEvent(
                new CustomEvent("onEdit", { detail: {} }),
              );
            }, 0);
          }}
        />
      </BoardCard>
    </>
  );
};

export const BoardCardColumn: React.FC<{
  colIndex: number;
  groupVal: string;
  pages: NotebookPage[];
  folderId: string;
}> = ({ groupVal, pages, folderId, colIndex }) => {
  const { setNodeRef, isOver } = useDroppable({ id: groupVal });
  const { isDarkMode } = useIsDarkMode();
  const isOverBackground = isDarkMode
    ? "rgba(255, 255, 255, 0.15)"
    : "rgba(0, 0, 0, 0.15)";

  return (
    <Box
      data-testid={`board-column-${groupVal}`}
      ref={setNodeRef}
      sx={{
        backgroundColor: isOver ? isOverBackground : "transparent",
        padding: "1rem",
        minWidth: "12rem",
        minHeight: "150px",
      }}
    >
      <Typography variant="h6">{groupVal}</Typography>
      {pages.map((page, rowIndex: number) => {
        const coord: Coord = {
          row: rowIndex,
          col: colIndex,
        };
        return (
          <BoardCardPage
            key={page["page-id"]}
            pageId={page["page-id"]}
            coord={coord}
            folderId={folderId}
          />
        );
      })}
      <BoardCardAddPage folderId={folderId} groupVal={groupVal} />
    </Box>
  );
};

export const BoardViewEmpty: React.FC<{}> = () => {
  return <>Empty</>;
};

export const BoardViewNotEmpty: React.FC<{}> = () => {
  const page = useSelectedNotebookPage();
  const getProperty = useGetProperty();
  const view = useSelectedDatabaseView();
  const getPropertyValue = useGetPropertyValue();
  const setPropertyValue = useSetPropertyValue();
  const folderId = page!["page-id"];
  const pages = useDatabasePages(folderId);
  const fetchPropertyValues = useFetchDatabasePropertyValues();

  useEffect(() => {
    if (!folderId || pages.length === 0) return;
    const pageIds = pages.map((p) => p["page-id"]);
    fetchPropertyValues({ folderId, pageIds });
  }, [folderId, pages.length]);

  const viewData = view! as BoardPageView;
  const viewName = viewData.name;
  const groupByPropId = viewData["group-by"]!;
  const pagesWithPropVal = pages.filter((page) => {
    const pageId = page["page-id"];
    return getPropertyValue(pageId, groupByPropId);
  });
  const { folderProps } = useFolderPropertyTypes(folderId);
  const prop = (folderProps ?? {})[groupByPropId];
  const propData = prop as PropertyTypeSelect;
  const groupVals = propData["options"].map((option) => option.label);
  const emptyGroupsMap = R.fromPairs(
    R.zip(groupVals, R.repeat([], R.length(groupVals))),
  );
  const filledGroupValsMap = R.groupBy((page) => {
    const pageId = page["page-id"];
    const propVal = getPropertyValue(pageId, groupByPropId)!;
    return typeof propVal === "string" ? propVal : propVal["prop-value"];
  }, pagesWithPropVal);
  const groupValsMap = { ...emptyGroupsMap, ...filledGroupValsMap };
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over) return;
    const targetVal = event.over!.id! as string;
    const pageId = event.active.id as string;
    setPropertyValue(pageId, prop.name, targetVal);
  };

  const navigationMapPages = Object.entries(groupValsMap).map((entry: any) => {
    const [groupVal, pages]: [string, NotebookPage[]] = entry;
    return [
      ...pages.map((page) => `${page["page-id"]}`),
      `add-page-${groupVal}`,
    ];
  });

  return (
    <>
      <Box data-testid="board-view" sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h4">{viewName}</Typography>
        <Box>
          <NotebookDatabaseToolBar />
        </Box>
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <KeyboardNavigationProvider
          navigationMap={navigationMapPages}
          columnMajor={true}
        >
          <Box data-testid="board-columns" sx={{ display: "flex", gap: "3rem", flexDirection: "row" }}>
            {Object.entries(groupValsMap).map(
              (entry: any, columnIndex: number) => {
                const [groupVal, pages] = entry;
                return (
                  <BoardCardColumn
                    colIndex={columnIndex}
                    folderId={folderId}
                    key={groupVal}
                    groupVal={groupVal}
                    pages={pages!}
                  />
                );
              },
            )}
          </Box>
        </KeyboardNavigationProvider>
      </DndContext>
    </>
  );
};

export const BoardView: React.FC<{}> = () => {
  useFetchDatabasePropertyValuesListener();
  useFetchDatabasePagesListener();
  const getProperty = useGetProperty();
  const view = useSelectedDatabaseView() as BoardPageView;
  const page = useSelectedNotebookPage();
  const folderId = page?.["page-id"];
  const groupByPropName = view["group-by"]!;
  const { folderProps } = useFolderPropertyTypes(folderId);
  const prop = (folderProps ?? {})[groupByPropName];
  return prop ? <BoardViewNotEmpty /> : <BoardViewEmpty />;
};

export const AddBoardViewMenuContentMenuItem: React.FC<{
  propName: string;
}> = ({ propName }) => {
  return <MenuItem value={propName}>{propName}</MenuItem>;
};

export const AddBoardViewMenuContent: React.FC<{
  viewData: PageView;
  setViewData: (viewData: PageView) => void;
  folderId: string;
}> = ({ viewData, setViewData, folderId }) => {
  const { folderProps } = useFolderPropertyTypes(folderId);
  const props = Object.values(folderProps ?? {});
  const getProp = useGetProperty();

  const boardViewData = viewData as BoardPageView;
  const setGroupBy = (propId: string) => {
    const newViewData: BoardPageView = {
      ...boardViewData,
      "group-by": propId,
    };
    setViewData(newViewData);
  };
  const groupBy = boardViewData["group-by"] ?? "";
  const selectProps = props.filter((prop) => {
    return prop["prop-type"] === PropertyTypeKey.SELECT;
  });

  return (
    <FormControl>
      <InputLabel id="board-group-by-label">Group By</InputLabel>
      <Select
        value={groupBy}
        labelId="board-group-by-label"
        label="Group By"
        onChange={(e: any) => {
          setGroupBy(e.target.value);
        }}
      >
        {selectProps?.map((prop) => {
          const name = prop.name;
          return (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
